import type { ColorMap, MapInfoValue, MapLayer } from '../models';
import { MapLayerType } from '../enums';
import { calculateColorVectorInfo, calculateGeoTiffColorInfo } from '.';
import { groupNumberByThree, integerPartOfNumber, roundToTenth } from '@core';

export const defaultMapInfoTitleFn = (layer: MapLayer) => {
  return layer.name;
};

export const defaultMapInfoSubTitleFn = () => {
  return '';
};

export const defaultMapInfoValueFn = (layer: MapLayer, title: string) => {
  return layer.data.unitType ? `${title} ${layer.data.unitType}` : title;
};

export const currencyMapInfoValueFn = (layer: MapLayer, title: string) => {
  return defaultMapInfoValueFn(layer, groupNumberByThree(integerPartOfNumber(title)));
};

export const decisionSupportYieldMapInfoValueFn = (layer: MapLayer, title: string) => {
  return `${(Number(title) / 1000).toFixed(2)} ${layer.data.unitType ? ` ${layer.data.unitType}` : ''}`;
};

export const tenthRoundedMapInfoValueFn = (layer: MapLayer, title: string) => {
  const numTitle = parseFloat(title);
  return isNaN(numTitle)
    ? defaultMapInfoValueFn(layer, title)
    : defaultMapInfoValueFn(layer, roundToTenth(numTitle).toString());
};

export const defaultStatisticsMapInfoValueFn = (layer: MapLayer, title: string) => {
  const numTitle = parseFloat(title);
  return isNaN(numTitle)
    ? defaultMapInfoValueFn(layer, title)
    : defaultMapInfoValueFn(layer, integerPartOfNumber(numTitle));
};

export const generateColorInfo = async (layerType: MapLayerType, values: number[], colorMap: ColorMap) => {
  switch (layerType) {
    case MapLayerType.GEOTIFF:
      return await calculateGeoTiffColorInfo(values, colorMap);
    default:
      return await calculateColorVectorInfo(values, colorMap);
  }
};

export const generateMapInfoValues = (
  layer: MapLayer,
  colorInfo: any,
  mapValueFn: (layer: MapLayer, title: string, i?: number) => string,
  valueDecimals = 2
) => {
  const colorBreaks: { color: string; value: number; title?: string; percent: number }[] = colorInfo.colorBreaks;
  const values: MapInfoValue[] = [];

  switch (layer.mapLayerType) {
    case MapLayerType.GEOTIFF:
      for (let i = 0; i < colorBreaks.length - 1; i++) {
        const start = colorBreaks[i].value.toFixed(valueDecimals);
        const stop = colorBreaks[i + 1].value.toFixed(valueDecimals);

        values.push({
          title: mapValueFn(layer, `${start} - ${stop}`),
          color: colorBreaks[i].color,
          percent: colorBreaks[i].percent
        });
      }
      break;
    default:
      for (let i = 0; i < colorBreaks.length; i++) {
        const value = colorBreaks[i].value.toFixed(valueDecimals);

        values.push({
          title: colorBreaks[i]?.title ?? mapValueFn(layer, `${value}`, i),
          color: colorBreaks[i].color,
          percent: colorBreaks[i].percent
        });
      }
  }

  return values;
};
